import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { SEDE_SCOPE, sedeScopeHeaders } from '../utils/sede-scope.util';
import { ReportsSedeScopeHolder, usesReportsSedeScope } from '../utils/reports-sede-scope';

/**
 * Adjunta el header X-Sede-Id en cada request si hay una sede activa seleccionada.
 *
 * Fuentes de la sede activa (en orden de prioridad):
 *  1. sessionStorage: 'pidepaya.activeSedeId' (owner/POS/Mesero)
 *  2. localStorage:   'pidepaya.activeSedeId' (persistencia entre sesiones del owner)
 *  3. localStorage:   'pidepaya.sede.{slug}' si la URL contiene /menu/{slug} (menú público)
 *
 * Los requests que ya traen X-Sede-Id NO se sobreescriben.
 * Los requests con alcance explicito (HttpContext `SEDE_SCOPE`, p.ej. el dashboard con varias
 * sedes) usan ESE alcance y no la sede activa global: ver `sedeScopeHeaders`.
 * Los requests de REPORTES usan el alcance que la pantalla de Reportes fijo (`ReportsSedeScopeHolder`),
 * salvo las rutas que el backend no admite con varias sedes (ver `usesReportsSedeScope`).
 * Solo se aplica a requests de la API de PidePaYa (environment.apiUrl).
 */
export const sedeInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.headers.has('X-Sede-Id')) {
    return next(req);
  }

  // Solo agregar X-Sede-Id a requests de la propia API. Evita mandarlo a
  // endpoints externos (WhatsApp API, MyNotify, CDN, etc.) que fallan el preflight CORS.
  if (!req.url.startsWith(environment.apiUrl)) {
    return next(req);
  }

  const scope = req.context.get(SEDE_SCOPE);
  if (scope !== null) {
    return next(req.clone({ setHeaders: sedeScopeHeaders(scope) }));
  }

  const reportsScope = inject(ReportsSedeScopeHolder).scope();
  if (reportsScope !== undefined && usesReportsSedeScope(req.url)) {
    return next(req.clone({ setHeaders: sedeScopeHeaders(reportsScope) }));
  }

  // La lista de sedes (GET .../sedes) devuelve las sedes accesibles del usuario y NO
  // se filtra por la sede activa (el handler ignora ISedeContext). Adjuntarle X-Sede-Id
  // solo la expone al enforcement del SedeContextMiddleware: si el valor guardado es una
  // sede a la que el usuario (p.ej. un Manager) no tiene acceso, el backend responde 403
  // y el selector se queda sin lista que renderizar. Como el header no cambia el resultado,
  // lo omitimos para que la lista siempre cargue (igual que la ve el Owner, exento del middleware).
  if (req.method === 'GET' && req.url.split('?')[0].endsWith('/sedes')) {
    return next(req);
  }

  let sedeId: string | null = null;

  try {
    sedeId = sessionStorage.getItem('pidepaya.activeSedeId')
      || localStorage.getItem('pidepaya.activeSedeId');

    if (!sedeId) {
      // Detecta el slug en cualquiera de las URLs publicas conocidas:
      // - /menu/<slug>          (legacy directo)
      // - /<slug>/page/menu     (vitrina con switcher de tema)
      // - /<slug>/linktree      (landing tipo linktree)
      // El primer grupo que matchee es el slug.
      const path = window.location.pathname;
      const slugFromPath =
        path.match(/^\/menu\/([^\/\?]+)/)?.[1]
        ?? path.match(/^\/([^\/\?]+)\/page\/menu/)?.[1]
        ?? path.match(/^\/([^\/\?]+)\/linktree/)?.[1];

      if (slugFromPath) {
        sedeId = localStorage.getItem(`pidepaya.sede.${slugFromPath}`);
      }
    }
  } catch {
    // sessionStorage/localStorage pueden fallar en modo privado/iframes restringidos
  }

  if (!sedeId) {
    return next(req);
  }

  const cloned = req.clone({ setHeaders: { 'X-Sede-Id': sedeId } });
  return next(cloned);
};
